import React, { useEffect, useState } from 'react';
import { Box, TextField, Typography, IconButton, FormHelperText, Divider, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useForm, useFieldArray } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { AppDispatch } from '../../stateManagement/store';
import { useDispatch } from 'react-redux';
import { dayList } from '../DateSelectable';
import CustomDialogTitle from '../../customComponents/CustomDialogTitle';
import CustomDialogContent from '../../customComponents/CustomDialogContent';
import CustomFormAction from '../../customComponents/CustomFormAction';
import CustomButton from '../../customComponents/CustomButton';
import InfoComponent from '../../customComponents/InfoComponent';
import { IServiceProviderAvailability, IServiceProviderAvailabilityRequest } from "@ontime/types";
import { useSelector } from 'react-redux';
import { selectSelectedOrganization } from '../../stateManagement/selectedOrganizationSlice';
import { selectSelectedServiceProvider } from '../../stateManagement/selectedServiceProviderSlice';
import { createServiceProviderAvailabilityAsync, updateServiceProviderAvailabilityAsync } from '../../stateManagement/serviceProviderAvailabilitySlice';

const availabilityDaySchema = z.object({
    dayOfWeek: z.number().min(0, "Day of the week is required."),
    availabilityStart: z.string().min(1, "Start time is required."),
    availabilityEnd: z.string().min(1, "End time is required."),
});

const serviceProviderAvailabilitySchema = z.object({
    serviceProviderAvailabilityId: z.string(),
    organizationId: z.string().optional(),
    serviceProviderId: z.string(),
    startDate: z.string().min(1, "Start Date cannot be empty."),
    endDate: z.string().optional(),
    serviceProviderAvailabilityDays: z.array(availabilityDaySchema).min(1, "At least one day is required."),
})

interface ServiceProviderAvailabilityFormProps {
    serviceProviderInput?: IServiceProviderAvailability | null;
    closeForm: () => void;
    organizationId: string;
    serviceProviderId: string;
}

const ServiceProviderAvailabilityForm: React.FC<ServiceProviderAvailabilityFormProps> = ({ serviceProviderInput, closeForm, organizationId, serviceProviderId }) => {

    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(true);

    const dispatch = useDispatch<AppDispatch>();

    const organization = useSelector(selectSelectedOrganization);
    const selectedServiceProvider = useSelector(selectSelectedServiceProvider);

    const { register, handleSubmit, control, formState: { errors }, reset, watch, setValue } = useForm<IServiceProviderAvailabilityRequest>({
        resolver: zodResolver(serviceProviderAvailabilitySchema),
        defaultValues: {
            serviceProviderAvailabilityId: '',
            serviceProviderId: selectedServiceProvider.serviceProviderId ?? '',
            startDate: new Date().toISOString().split('T')[0],
            endDate: undefined,
            serviceProviderAvailabilityDays: [
                { dayOfWeek: 1, availabilityStart: '07:00:00', availabilityEnd: '23:00:00', },
            ],
        },
    });

    const { fields, append, remove, update } = useFieldArray({ control, name: "serviceProviderAvailabilityDays" });

    useEffect(() => {
        if (serviceProviderInput?.serviceProviderAvailabilityId) {
            reset({
                serviceProviderAvailabilityId: serviceProviderInput?.serviceProviderAvailabilityId,
                serviceProviderId: selectedServiceProvider.serviceProviderId ?? '',
                startDate: serviceProviderInput.startDate.split('T')[0],
                endDate: serviceProviderInput.endDate ? serviceProviderInput.endDate.split('T')[0] : '',
                serviceProviderAvailabilityDays:
                    serviceProviderInput.serviceProviderAvailabilityDays.map(item => ({
                        dayOfWeek: item.dayOfWeek,
                        availabilityStart: item.availabilityStart,
                        availabilityEnd: item.availabilityEnd,
                    }))
            });
        }
        else {
            reset({
                serviceProviderAvailabilityId: '',
                serviceProviderId: selectedServiceProvider.serviceProviderId ?? '',
                startDate: new Date().toISOString().split('T')[0],
                endDate: undefined,
                serviceProviderAvailabilityDays: [
                    { dayOfWeek: 1, availabilityStart: '07:00:00', availabilityEnd: '23:00:00', },
                ],
            });
        }
        setLoading(false);
    }, [serviceProviderInput, reset]);

    const handleSave = async (data: IServiceProviderAvailabilityRequest) => {

        setLoading(true);
        try {
            // Normalize empty endDate ("") to undefined so the API doesn't receive an empty string
            if (!data.endDate || String(data.endDate).trim() === '') {
                data.endDate = undefined;
            }
            let result;
            if (serviceProviderInput?.serviceProviderAvailabilityId) {
                result = await dispatch(updateServiceProviderAvailabilityAsync({ organizationId: organization.organizationId ?? '', serviceProviderId: selectedServiceProvider.serviceProviderId ?? '', data })).unwrap();
            } else {
                result = await dispatch(createServiceProviderAvailabilityAsync({ organizationId: organization.organizationId ?? '', serviceProviderId: selectedServiceProvider.serviceProviderId ?? '', data })).unwrap();
            }
            if (result) {
                closeForm();
            }
        } catch (error) {
            console.error(error, { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleDay = (day: { id: number; title: string }) => {
        const dayFields = fields
            .map((field, index) => ({ ...field, index }))
            .filter((field) => field.dayOfWeek === day.id);

        const elements: React.ReactNode[] = [];

        dayFields.forEach(({ index }) => {
            elements.push(
                <>
                    <Box key={index} mt={2} display="flex" alignItems="center" >
                        <TextField
                            label="Start Time"
                            InputLabelProps={{ shrink: true }}
                            type="time"
                            size="small"
                            {...register(`serviceProviderAvailabilityDays.${index}.availabilityStart`, {
                                setValueAs: (v) => (v && v.length === 5 ? `${v}:00` : v), // add seconds if missing
                            })}
                            error={!!errors.serviceProviderAvailabilityDays?.[index]?.availabilityStart}
                            helperText={errors.serviceProviderAvailabilityDays?.[index]?.availabilityStart?.message}
                            style={{ marginLeft: '3px', maxWidth: '137px' }}
                        />

                        <TextField
                            label="End Time"
                            InputLabelProps={{ shrink: true }}
                            type="time"
                            size="small"
                            {...register(`serviceProviderAvailabilityDays.${index}.availabilityEnd`, {
                                setValueAs: (v) => (v && v.length === 5 ? `${v}:00` : v), // add seconds if missing
                            })}
                            error={!!errors.serviceProviderAvailabilityDays?.[index]?.availabilityEnd}
                            helperText={errors.serviceProviderAvailabilityDays?.[index]?.availabilityEnd?.message}
                            style={{ marginLeft: '3px', maxWidth: '136px' }}
                        />

                        <InfoComponent infoContent='Remove this time period'>
                            <IconButton
                                onClick={() => remove(index)}
                                style={{ marginLeft: '3px', marginRight: '1px' }}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </InfoComponent>
                    </Box>
                    {/* {errors.serviceProviderAvailabilityDays?.[index]?.timeType && (
                        <FormHelperText sx={{ color: 'red' }}>
                            {errors.serviceProviderAvailabilityDays[index]?.timeType?.message}
                        </FormHelperText>
                    )} */}
                </>
            );
        });

        return elements;
    };

    return (
        <Box>
            <CustomDialogTitle handleClose={closeForm} title={serviceProviderInput?.serviceProviderAvailabilityId ? 'Edit availability' : 'New availability'} loading={loading} />
            <CustomDialogContent>
                <form onSubmit={handleSubmit(handleSave)} noValidate>
                    <Box display="flex" alignItems="center" gap={2} mt={2}>
                        <TextField fullWidth label="Start Date" InputLabelProps={{ shrink: true }} type="date" {...register('startDate')} error={!!errors.startDate} helperText={errors.startDate?.message} />

                        <TextField
                            fullWidth
                            label="End Date"
                            InputLabelProps={{ shrink: true }}
                            type="date"
                            {...register('endDate', { setValueAs: (v) => (v === '' ? undefined : v) })}
                            error={!!errors.endDate}
                            helperText={errors.endDate?.message}
                        />
                    </Box>
                    <Typography variant="h6" mt={4}>Availability Days</Typography>
                    {fields.length === 0 && <FormHelperText error sx={{ ml: 2 }}>At least one day is required</FormHelperText>}
                    {dayList.map((day) =>
                        <React.Fragment key={day.id}>
                            <Divider />
                            <Box sx={{ margin: '5px 0' }} display="flex" flexDirection='column'>
                                <Box display='flex' flexDirection='row' justifyContent='flex-start' alignItems='center' sx={{ minWidth: '80px' }} >
                                    <Typography variant="body1" >{day.title}</Typography>
                                    <InfoComponent infoContent={`Add a new availability period on ${day.title}`}>
                                        <IconButton
                                            onClick={() =>
                                                append({
                                                    dayOfWeek: day.id,
                                                    availabilityStart: '07:00:00',
                                                    availabilityEnd: '23:00:00',
                                                    // timeType: ServiceProviderAvailabilityDayType.PublicAccess
                                                })
                                            }
                                            style={{ marginLeft: '1px', marginRight: '1px' }}
                                        >
                                            <AddIcon />
                                        </IconButton>
                                    </InfoComponent>
                                </Box>
                                <Box>{handleDay(day)}</Box>
                            </Box>
                        </React.Fragment>
                    )}
                    <CustomFormAction>
                        <CustomButton lable='Save' type='submit' disabled={loading} infoContent={`Save the ${serviceProviderInput?.serviceProviderAvailabilityId ? 'updated' : 'new'} availability schedule`} />
                    </CustomFormAction>
                </form>
            </CustomDialogContent>
        </Box>
    );
};

export default ServiceProviderAvailabilityForm;